import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Card, CardBody, CardFooter, CardHeader } from '@/src/elements/card'
import { Typography } from '@/src/elements/text/Typography'
import { useIntentionForm } from '@/src/features/intention/ui/hooks/useIntentionForm'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTrackDisplayed } from '@/src/general/hooks/useTrackOnce'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { useCommunityQuery } from '../../../services/useCommunityQuery'
import { CommunityShareModal } from '../../components/CommunityShareModal'
import { SubscribeJourneyLayout } from '../../layouts/SubscribeJourneyLayout'
import { CommunityRandomIntention } from './CommunityRandomIntention'

type TCommunityJoinNextSuggestionsProps = {
  communityId: number
}

export const CommunityJoinNextSuggestions = ({ communityId }: TCommunityJoinNextSuggestionsProps): ReactNode => {
  const { data: community } = useCommunityQuery(communityId)
  const router = useRouter()
  const { routes } = useRoutes()

  useTrackDisplayed(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_DISPLAYED, communityId, {
    communityInfos: { community_id: communityId },
  })

  const intentionForm = useIntentionForm({
    // For future task: buttonText will change based on the intentionLandingPage data
    title: <FormattedMessage id="pray.entrust-intention" values={{ buttonText: '' }} />,
    description: <FormattedMessage id="pray.entrust-intention.body" />,
    onDisplay: () => {
      GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_LEAVE_INTENTION_DISPLAYED, {
        communityInfos: { community_id: communityId },
      })
    },
    onSubmit: () => {
      GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_LEAVE_INTENTION_SUBMITTED, {
        communityInfos: { community_id: communityId },
      })
      GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_CTA_CLICKED, {
        communityInfos: { community_id: communityId },
      })
    },
  })

  const handlePress = () => {
    GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_CLOSED, {
      communityInfos: { community_id: communityId },
    })
    router.navigate(routes['prayer-space']())
  }

  if (!community) return null

  return (
    <SubscribeJourneyLayout
      title={
        <FormattedMessage id="community.join-next.no-publication.start" values={{ lifetime: community?.lifetime }} />
      }
      showBackButton
      footer={
        <Button endIcon={ICON.CHEVRON_RIGHT} variant="light" colors="primary" onPress={handlePress}>
          <FormattedMessage id="word.continue" />
        </Button>
      }
    >
      <View className="gap-4">
        <Card className="w-full gap-4 bg-secondary-bg-medium p-6" shadow="xs" testID={TEST_ID.COMMUNITY.SHARE_CARD}>
          <CardHeader className="p-0">
            <Typography colors="secondary" contrast="default" size="lg" bold>
              <FormattedMessage id="share.community.share" values={{ lifetime: community?.lifetime }} />
            </Typography>
          </CardHeader>
          <CardBody className="p-0">
            <Typography colors="secondary" contrast="default" size="md">
              <FormattedMessage id="community.share.text" />
            </Typography>
          </CardBody>
          <CardFooter className="p-0">
            <CommunityShareModal {...community} trackingSource={TRACKING_SOURCE.COMMUNITY_PAGE}>
              {({ isLoading, onShare }) => (
                <Button
                  variant="solid"
                  colors="secondary"
                  fullWidth
                  startIcon={ICON.SEND}
                  testID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.COMMUNITY_BUTTON}
                  onPress={() => {
                    GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_SHARE_CLICKED, {
                      communityInfos: { community_id: communityId },
                    })
                    GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_CTA_CLICKED, {
                      communityInfos: { community_id: communityId },
                    })
                    onShare()
                  }}
                  isLoading={isLoading}
                >
                  <FormattedMessage id="wording.i-share" />
                </Button>
              )}
            </CommunityShareModal>
          </CardFooter>
        </Card>
        <CommunityRandomIntention
          community={community}
          testID={TEST_ID.COMMUNITY.JOIN_NEXT.INTENTION.PRAY}
          onPray={() => {
            GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_PRAY_INTENTION_CLICKED, {
              communityInfos: { community_id: communityId },
            })
            GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_NOT_STARTED_CTA_CLICKED, {
              communityInfos: { community_id: communityId },
            })
          }}
        />
        {intentionForm}
      </View>
    </SubscribeJourneyLayout>
  )
}
